import { cookieStorage } from '../utils/cookieStorage';
import React, {createContext, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {fetchRoutePermissions, RoutePerm} from '../services/permissionService';
import {useAuth} from './AuthContext';

type Change = {permission?: RoutePerm; deletedId?: number};
interface PermissionsContextType {
    permissions: Record<string,string[]>;
    disabledPaths: string[];
    loading: boolean;
}
const PermissionsContext = createContext<PermissionsContextType>({permissions:{}, disabledPaths:[], loading:true});

export const PermissionsProvider: React.FC<{children:React.ReactNode}> = ({children}) => {
    const {isAuthenticated, user} = useAuth();
    const [rules, setRules] = useState<RoutePerm[]>([]);
    const [loading, setLoading] = useState(true);
    const [revision, setRevision] = useState(0);
    const generation = useRef(0);
    const owner = useRef<number | null>(null);
    const authenticated = useRef(isAuthenticated);
    authenticated.current = isAuthenticated;

    useEffect(() => {
        let cookieRevision = cookieStorage.getItem('comarca_route_permissions_revision');
        const apply = (change?: Change) => {
            if (!authenticated.current) return;
            if (!change?.permission && change?.deletedId === undefined) {setRevision(value => value + 1); return;}
            generation.current += 1;
            setRules(current => change.permission
                ? [...current.filter(rule => rule.id !== change.permission!.id), change.permission]
                : current.filter(rule => rule.id !== change.deletedId));
        };
        const changed = (event: Event) => {
            const detail = (event as CustomEvent<Change>).detail;
            apply(detail);
            cookieRevision = cookieStorage.getItem('comarca_route_permissions_revision');
        };
        const focused = () => setRevision(value => value + 1);
        const timer = window.setInterval(() => {
            const current = cookieStorage.getItem('comarca_route_permissions_revision');
            if (current !== cookieRevision) {cookieRevision = current; setRevision(value => value + 1);}
        }, 500);
        window.addEventListener('route-permissions-changed', changed);
        window.addEventListener('focus', focused);
        return () => {
            window.clearInterval(timer);
            window.removeEventListener('route-permissions-changed', changed);
            window.removeEventListener('focus', focused);
        };
    }, []);

    useEffect(() => {
        let active = true;
        const version = generation.current += 1;
        if (!isAuthenticated) {
            owner.current = null;
            setRules([]);
            setLoading(false);
            return;
        }
        if (owner.current !== user?.id) {
            owner.current = user?.id ?? null;
            setRules([]);
            setLoading(true);
        }
        fetchRoutePermissions().then(list => {
            if (!active || generation.current !== version) return;
            setRules(list);
            setLoading(false);
        }).catch(error => {
            if (!active || generation.current !== version) return;
            setRules([]);
            setLoading(false);
            console.error(error);
        });
        return () => {active = false;};
    }, [isAuthenticated, user?.id, revision]);

    const permissions = useMemo(() => Object.fromEntries(rules.map(rule => [rule.path, rule.activo === false ? [] : rule.allowed_roles])), [rules]);
    const disabledPaths = useMemo(() => rules.filter(rule => rule.activo === false).map(rule => rule.path), [rules]);
    return <PermissionsContext.Provider value={{permissions, disabledPaths, loading}}>{children}</PermissionsContext.Provider>;
};
export const usePermissions = () => useContext(PermissionsContext);
